import { useState, useEffect } from "react";
import { Row, Col, Spinner, Alert, Table } from "react-bootstrap";
import PeriodFilter from "../PeriodFilter";
import FuncionarioFilter from "../FuncionarioFilter";
import StatCard from "../StatCard";
import CustomPeriodModal from "../CustomPeriodModal";
import EvolucionFuncionariosChart from "../EvolucionFuncionariosChart";
import {
  obtenerListaFuncionariosJefatura,
  obtenerEstadisticasFuncionarios,
  obtenerDesempenoPorFuncionario,
} from "../../../services/apis_funcionarios";
import { obtenerEvolucionPorFuncionario } from "../../../services/apis_jefatura";

function FuncionariosTab() {
  const [selectedPeriod, setSelectedPeriod] = useState("last_month");
  const [selectedFuncionarios, setSelectedFuncionarios] = useState([]);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customDates, setCustomDates] = useState(null);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFuncionarios, setLoadingFuncionarios] = useState(true);
  const [error, setError] = useState(null);
  const [desempenoData, setDesempenoData] = useState([]);
  const [evolucionData, setEvolucionData] = useState(null);
  const [loadingEvolucion, setLoadingEvolucion] = useState(false);

  // Estado para las estadísticas
  const [stats, setStats] = useState({
    totalFuncionarios: {
      value: "0",
      subtitle: "En el período",
    },
    promedioAtencionesDia: {
      value: "0.0",
      subtitle: "Min: 0.0 | Máx: 0.0",
    },
    tiempoPromedioAtencion: {
      value: "0 min",
      subtitle: "Min: 0 min | Máx: 0 min",
    },
  });

  // Cargar lista de funcionarios al montar el componente
  useEffect(() => {
    const cargarFuncionarios = async () => {
      setLoadingFuncionarios(true);
      try {
        const data = await obtenerListaFuncionariosJefatura();
        setFuncionarios(data);
      } catch (err) {
        console.error("Error cargando funcionarios:", err);
        setError("No se pudo cargar la lista de funcionarios.");
      } finally {
        setLoadingFuncionarios(false);
      }
    };

    cargarFuncionarios();
  }, []);

  // Cargar estadísticas cuando cambien los filtros
  useEffect(() => {
    const cargarEstadisticas = async () => {
      setLoading(true);
      setError(null);

      const params = {};

      if (selectedPeriod !== "custom") {
        params.period = selectedPeriod;
      } else if (customDates) {
        params.startDate = customDates.startDate;
        params.endDate = customDates.endDate;
      }

      if (selectedFuncionarios && selectedFuncionarios.length > 0) {
        params.funcionarioIds = selectedFuncionarios.join(',');
      }

      // Solo cargar si tenemos un período válido
      if (params.period || (params.startDate && params.endDate)) {
        try {
          const [statsData, desempenoResponse] = await Promise.all([
            obtenerEstadisticasFuncionarios(params),
            obtenerDesempenoPorFuncionario(params)
          ]);

          setStats({
            totalFuncionarios: {
              value: statsData.total_funcionarios?.toString() || "0",
              subtitle: "En el período",
            },
            promedioAtencionesDia: {
              value: statsData.promedio_atenciones_dia?.toString() || "0.0",
              subtitle: `Min: ${statsData.min_atenciones_dia || 0.0} | Máx: ${statsData.max_atenciones_dia || 0.0}`,
            },
            tiempoPromedioAtencion: {
              value: statsData.tiempo_promedio_texto || "0 min",
              subtitle: `Min: ${statsData.min_tiempo_atencion || 0} min | Máx: ${statsData.max_tiempo_atencion || 0} min`,
            },
          });

          setDesempenoData(desempenoResponse.funcionarios || []);
        } catch (err) {
          console.error("Error cargando estadísticas:", err);
          setError("No se pudieron cargar las estadísticas. Intenta nuevamente.");
        } finally {
          setLoading(false);
        }
      }
    };

    cargarEstadisticas();
  }, [selectedPeriod, customDates, selectedFuncionarios]);

  // Cargar datos de evolución por funcionario
  useEffect(() => {
    const cargarEvolucion = async () => {
      setLoadingEvolucion(true);

      const params = {};

      if (selectedPeriod !== "custom") {
        params.period = selectedPeriod;
      } else if (customDates) {
        params.startDate = customDates.startDate;
        params.endDate = customDates.endDate;
      }

      if (selectedFuncionarios && selectedFuncionarios.length > 0) {
        params.funcionarioIds = selectedFuncionarios.join(',');
      }

      if (params.period || (params.startDate && params.endDate)) {
        try {
          const data = await obtenerEvolucionPorFuncionario(params);
          setEvolucionData(data);
        } catch (err) {
          console.error("Error cargando evolución:", err);
        } finally {
          setLoadingEvolucion(false);
        }
      }
    };

    cargarEvolucion();
  }, [selectedPeriod, customDates, selectedFuncionarios]);
  const handlePeriodChange = (period) => {
    if (period === "custom") {
      setShowCustomModal(true);
    } else {
      setSelectedPeriod(period);
      setCustomDates(null);
    }
  };

  const handleCustomPeriodApply = (dates) => {
    setSelectedPeriod("custom");
    setCustomDates(dates);
  };

  const handleCustomModalClose = () => {
    setShowCustomModal(false);
    if (selectedPeriod === "custom" && !customDates) {
      setSelectedPeriod("last_month");
    }
  };

  return (
    <div>
      {/* Filtros */}
      <Row className="mb-4">
        <Col xs={12} md={8}>
          <PeriodFilter
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
          />
        </Col>
        <Col xs={12} md={4} className="mt-3 mt-md-0">
          <FuncionarioFilter
            funcionarios={funcionarios}
            selectedFuncionarios={selectedFuncionarios}
            onFuncionariosChange={setSelectedFuncionarios}
            loading={loadingFuncionarios}
          />
        </Col>
      </Row>

      {/* Mostrar error si existe */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Mostrar spinner mientras carga */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
          <p className="mt-3 text-muted">Cargando estadísticas...</p>
        </div>
      )}

      {/* Mostrar estadísticas cuando no está cargando */}
      {!loading && (
        <Row className="g-4">
          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Total de funcionarios"
              value={stats.totalFuncionarios.value}
              subtitle={stats.totalFuncionarios.subtitle}
              icon="bi bi-people-fill"
              iconColor="blue"
              valueColor="blue"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Promedio de atenciones por día"
              value={stats.promedioAtencionesDia.value}
              subtitle={stats.promedioAtencionesDia.subtitle}
              icon="bi bi-bar-chart-line"
              iconColor="green"
              valueColor="green"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Promedio de tiempo por atención"
              value={stats.tiempoPromedioAtencion.value}
              subtitle={stats.tiempoPromedioAtencion.subtitle}
              icon="bi bi-clock-history"
              iconColor="cyan"
              valueColor="cyan"
            />
          </Col>
        </Row>
      )}

      {/* Gráfico de Evolución Temporal de Atenciones por Funcionario */}
      {!loading && (
        <Row className="mt-4">
          <Col>
            {loadingEvolucion ? (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Cargando...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Cargando gráfico de evolución...</p>
                </div>
              </div>
            ) : (
              <EvolucionFuncionariosChart
                data={evolucionData}
                period={selectedPeriod}
                customDates={customDates}
              />
            )}
          </Col>
        </Row>
      )}

      {/* Tabla de desempeño por funcionario */}
      {!loading && desempenoData.length > 0 && (
        <div className="mt-5">
          <h5 className="mb-3">Desempeño por Funcionario</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Funcionario</th>
                <th>Total Atenciones</th>
                <th>Tiempo Promedio</th>
                <th>Tiempo Mínimo</th>
                <th>Tiempo Máximo</th>
              </tr>
            </thead>
            <tbody>
              {desempenoData.map((func, index) => (
                <tr key={index}>
                  <td>{func.nombre}</td>
                  <td>{func.total_atenciones}</td>
                  <td>{func.tiempo_promedio} min</td>
                  <td style={{ color: '#28a745' }}>{func.tiempo_minimo} min</td>
                  <td style={{ color: '#ffc107' }}>{func.tiempo_maximo} min</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <CustomPeriodModal
        show={showCustomModal}
        onHide={handleCustomModalClose}
        onApply={handleCustomPeriodApply}
      />
    </div>
  );
}

export default FuncionariosTab;
